import { useMemo, useState } from "react";
import { Plus, Trash2, ReceiptText, Loader2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import MenuCombobox from "./MenuCombobox";
import QuantityStepper from "./QuantityStepper";
import { useMenu, formatINR } from "../lib/MenuContext";
import { apiCreateOrder, apiUpdateOrder } from "../lib/api";

const PAYMENT_METHODS = ["Cash", "GPay", "Pending Payment", "Custom", "Owner", "GPay+Cash"];
const PARTIAL_PAY_METHODS = new Set(["Cash", "GPay", "Custom", "GPay+Cash"]);

export default function OrderEntry({ editing = null, onSaved, onCancelEdit }) {
  const { menuById, refresh: refreshMenu, loading: menuLoading } = useMenu();
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const [lines, setLines] = useState(() =>
    editing
      ? (editing.items || []).map((it) => ({ item_id: it.item_id, qty: it.qty }))
      : []
  );
  const [isPendingOrder, setIsPendingOrder] = useState(
    () => editing?.payment_method === "Pending Payment" || false
  );
  const [paymentMethod, setPaymentMethod] = useState(
    () => (editing && editing.payment_method) || "Cash"
  );
  const [customPayment, setCustomPayment] = useState(
    () => (editing && editing.custom_payment) || ""
  );
  const [fullyPaid, setFullyPaid] = useState(() => {
    if (!editing) return true;
    const t = Number(editing.total || 0);
    const p = Number(editing.paid ?? t);
    const partial = PARTIAL_PAY_METHODS.has(editing.payment_method) && p < t;
    return !partial;
  });
  const [paidInput, setPaidInput] = useState(() => {
    if (!editing) return "";
    const t = Number(editing.total || 0);
    const p = Number(editing.paid ?? t);
    const partial = PARTIAL_PAY_METHODS.has(editing.payment_method) && p < t;
    return partial ? String(p) : "";
  });
  const [gPayAmount, setGPayAmount] = useState(() =>
    editing && editing.payment_method === "GPay+Cash" ? String(editing.gpay_amount || "") : ""
  );
  const [cashAmount, setCashAmount] = useState(() =>
    editing && editing.payment_method === "GPay+Cash" ? String(editing.cash_amount || "") : ""
  );
  const [saving, setSaving] = useState(false);

  const handlePendingOrderToggle = (next) => {
    setIsPendingOrder(next);
    if (next) {
      // When toggling ON, default to Pending Payment but user can change it
      setPaymentMethod("Pending Payment");
      setFullyPaid(true);
      setPaidInput("");
      setGPayAmount("");
      setCashAmount("");
    }
    // When toggling OFF, leave payment method as-is
  };

  const changePaymentMethod = (next) => {
    setPaymentMethod(next);
    if (!PARTIAL_PAY_METHODS.has(next)) {
      setFullyPaid(true);
      setPaidInput("");
    }
    if (next !== "GPay+Cash") {
      setGPayAmount("");
      setCashAmount("");
    }
    // Do NOT change isPendingOrder when payment method changes
  };

  const handleAddItem = () => {
    if (!itemId) { toast.error("Pick an item first"); return; }
    const q = parseInt(qty, 10);
    if (!q || q < 1) { toast.error("Quantity must be at least 1"); return; }
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.item_id === itemId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + q };
        return next;
      }
      return [...prev, { item_id: itemId, qty: q }];
    });
    setItemId("");
    setQty(1);
  };

  const handleRemove = (id) => setLines((prev) => prev.filter((l) => l.item_id !== id));

  const handleQtyChange = (id, q) => {
    if (q === "" || q === null || q === undefined) return;
    const n = typeof q === "number" ? q : parseInt(q, 10);
    if (!Number.isFinite(n) || n < 1) return;
    setLines((prev) => prev.map((l) => (l.item_id === id ? { ...l, qty: n } : l)));
  };

  const computed = useMemo(() => {
    const rows = lines.map((l) => {
      const m = menuById[l.item_id];
      const line_total = (m?.price || 0) * l.qty;
      return { ...l, name: m?.name || l.item_id, price: m?.price || 0, line_total };
    });
    const total = rows.reduce((sum, r) => sum + r.line_total, 0);
    return { rows, total };
  }, [lines, menuById]);

  const handleSave = async () => {
    if (lines.length === 0) { toast.error("Add at least one item"); return; }
    if (paymentMethod === "Custom" && !customPayment.trim()) {
      toast.error("Enter a label for the custom payment method"); return;
    }

    let paid;
    let gpay_amount;
    let cash_amount;

    if (paymentMethod === "Owner" || paymentMethod === "Pending Payment") {
      paid = 0;
    } else if (paymentMethod === "GPay+Cash") {
      gpay_amount = Number(gPayAmount) || 0;
      cash_amount = Number(cashAmount) || 0;
      paid = gpay_amount + cash_amount;
    } else if (PARTIAL_PAY_METHODS.has(paymentMethod) && !fullyPaid) {
      const parsed = Number(paidInput);
      if (!Number.isFinite(parsed) || parsed < 0) { toast.error("Enter a valid paid amount"); return; }
      paid = parsed;
    } else {
      paid = computed.total;
    }

    setSaving(true);
    try {
      const payload = {
        items: lines.map((l) => ({ item_id: l.item_id, qty: l.qty })),
        payment_method: paymentMethod,
        custom_payment: paymentMethod === "Custom" ? customPayment.trim() : "",
        paid,
        ...(paymentMethod === "GPay+Cash" && { gpay_amount, cash_amount }),
      };
      if (editing) {
        await apiUpdateOrder(editing.id, payload);
        toast.success("Order updated");
      } else {
        await apiCreateOrder(payload);
        toast.success("Order saved");
      }
      setLines([]);
      setPaymentMethod("Cash");
      setCustomPayment("");
      setFullyPaid(true);
      setPaidInput("");
      setGPayAmount("");
      setCashAmount("");
      setIsPendingOrder(false);
      setItemId("");
      setQty(1);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFullyPaid = (next) => { setFullyPaid(next); if (next) setPaidInput(""); };
  const handlePaidInputChange = (val) => { setPaidInput(val); if (val !== "" && fullyPaid) setFullyPaid(false); };

  const showPaidUI = PARTIAL_PAY_METHODS.has(paymentMethod) && paymentMethod !== "GPay+Cash";
  const effectivePaid =
    paymentMethod === "GPay+Cash"
      ? (Number(gPayAmount) || 0) + (Number(cashAmount) || 0)
      : showPaidUI
      ? fullyPaid ? computed.total : Number(paidInput) || 0
      : paymentMethod === "Owner" || paymentMethod === "Pending Payment"
      ? 0
      : computed.total;
  const diff = effectivePaid - computed.total;
  const outstanding = diff < 0 ? Math.abs(diff) : 0;
  const change = diff > 0 ? diff : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: item entry */}
      <div className="lg:col-span-2 bg-[#FFFCF5] border border-[#E6E2D8] rounded-lg p-6 sm:p-8 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight text-[#1A1918] font-heading">
              {editing ? "Edit Order" : "New Order"}
            </h2>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854] mt-1">
              Add items to the receipt
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await refreshMenu(true); toast.success("Menu refreshed from Google Sheet"); }}
              data-testid="refresh-menu-button"
              className="text-[#5C5854] hover:text-[#1A1918] hover:bg-[#F5F2EB]"
            >
              <RefreshCw size={14} className={menuLoading ? "animate-spin mr-1.5" : "mr-1.5"} />
              <span className="hidden sm:inline text-xs font-bold uppercase tracking-wider">Menu</span>
            </Button>
            {editing && (
              <Button variant="ghost" onClick={onCancelEdit} data-testid="cancel-edit-button" className="text-[#5C5854] hover:text-[#1A1918]">
                Cancel edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div className="flex flex-col gap-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">Menu item</Label>
            <MenuCombobox value={itemId} onChange={setItemId} />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">Qty</Label>
            <QuantityStepper value={qty} onChange={setQty} min={1} testid="quantity-input" />
          </div>
          <Button onClick={handleAddItem} data-testid="add-item-button" className="bg-[#1A1918] hover:bg-[#000] text-white font-bold h-12 px-5 rounded-lg">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>

        <div className="rounded-lg border border-[#E6E2D8] overflow-hidden">
          <div className="bg-[#F5F2EB] border-b border-[#E6E2D8] grid grid-cols-[1fr_90px_110px_110px_44px] text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] px-4 py-3">
            <div>Item</div><div className="text-right">Price</div><div className="text-center">Qty</div><div className="text-right">Line</div><div></div>
          </div>
          {computed.rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#5C5854]">No items added yet. Use the search above.</div>
          ) : (
            computed.rows.map((row) => (
              <div key={row.item_id} className="grid grid-cols-[1fr_90px_140px_110px_44px] items-center px-4 py-3 border-b border-[#E6E2D8] last:border-0" data-testid={`line-row-${row.item_id}`}>
                <div className="text-sm font-semibold text-[#1A1918] truncate pr-2">{row.name}</div>
                <div className="text-sm text-right text-[#5C5854]">{formatINR(row.price)}</div>
                <div className="flex justify-center">
                  <QuantityStepper value={row.qty} onChange={(n) => handleQtyChange(row.item_id, n)} min={1} size="sm" testid={`line-qty-${row.item_id}`} />
                </div>
                <div className="text-sm font-bold text-right text-[#1A1918]">{formatINR(row.line_total)}</div>
                <button type="button" onClick={() => handleRemove(row.item_id)} className="justify-self-end h-9 w-9 rounded-md flex items-center justify-center text-[#5C5854] hover:bg-[#F44336]/10 hover:text-[#F44336] transition-colors border border-transparent hover:border-[#F44336]/30" data-testid={`line-remove-${row.item_id}`} aria-label="Remove item">
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right: receipt */}
      <div className="bg-white border border-[#E6E2D8] rounded-lg p-6 sm:p-8 flex flex-col gap-5 h-fit lg:sticky lg:top-24">
        <div className="flex items-center gap-2">
          <ReceiptText size={18} className="text-[#C84C31]" />
          <h3 className="text-xl font-bold text-[#1A1918] font-heading">Live Receipt</h3>
        </div>

        <div className="flex flex-col gap-2 text-sm">
          {computed.rows.length === 0 ? (
            <p className="text-[#5C5854]">Receipt will appear here.</p>
          ) : (
            computed.rows.map((row) => (
              <div key={row.item_id} className="flex justify-between items-center gap-2">
                <span className="text-[#1A1918] flex-1 truncate">{row.qty} × {row.name}</span>
                <span className="text-[#1A1918] font-semibold tabular-nums">{formatINR(row.line_total)}</span>
                <button type="button" onClick={() => handleRemove(row.item_id)} data-testid={`receipt-remove-${row.item_id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-[#5C5854] hover:bg-[#F44336]/10 hover:text-[#F44336] transition-colors shrink-0">
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-dashed border-[#E6E2D8] pt-4 flex justify-between items-baseline">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854]">Total</span>
          <span data-testid="live-total-display" className="text-3xl font-black tabular-nums text-[#1A1918] font-heading">{formatINR(computed.total)}</span>
        </div>

        <div className="flex flex-col gap-3">
          {/* Pending Order Toggle */}
          <div className="flex items-center justify-between gap-3 pb-2 border-b border-dashed border-[#E6E2D8]">
            <Label htmlFor="pending-order-toggle" className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854] cursor-pointer">
              Pending Order
            </Label>
            <Switch
              id="pending-order-toggle"
              checked={isPendingOrder}
              onCheckedChange={handlePendingOrderToggle}
              data-testid="pending-order-toggle"
              className="data-[state=checked]:bg-[#F5A623]"
            />
          </div>

          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">Payment method</Label>
          <Select value={paymentMethod} onValueChange={changePaymentMethod}>
            <SelectTrigger data-testid="payment-method-dropdown" className="bg-white border-[#E6E2D8] h-11 text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFCF5] border-[#E6E2D8]">
              {PAYMENT_METHODS.map((p) => (
                <SelectItem key={p} value={p} data-testid={`payment-option-${p}`}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {paymentMethod === "Custom" && (
            <Input placeholder="e.g. Swiggy, Zomato, Voucher" value={customPayment} onChange={(e) => setCustomPayment(e.target.value)} data-testid="custom-payment-input" className="bg-white border-[#E6E2D8] h-11" />
          )}

          {paymentMethod === "Owner" && (
            <div data-testid="owner-payment-note" className="bg-[#5B7059]/10 border border-[#5B7059]/30 rounded-md px-3 py-2 text-xs text-[#1A1918]">
              Owner orders are <span className="font-bold">excluded from revenue</span>, but items are still counted in Items Sold.
            </div>
          )}

          {paymentMethod === "Pending Payment" && (
            <div className="bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-md px-3 py-2 text-xs text-[#1A1918]">
              This order will be <span className="font-bold">excluded from revenue</span> and shown in Pending Orders.
            </div>
          )}

          {paymentMethod === "GPay+Cash" && (
            <div className="flex flex-col gap-3 pt-2 border-t border-dashed border-[#E6E2D8]">
              <div className="flex flex-col gap-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">Paid with GPay</Label>
                <Input type="number" inputMode="decimal" min={0} step="any" placeholder="0" value={gPayAmount} onChange={(e) => setGPayAmount(e.target.value)} data-testid="gpay-amount-input" className="bg-white border-[#E6E2D8] h-11 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">Paid with Cash</Label>
                <Input type="number" inputMode="decimal" min={0} step="any" placeholder="0" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} data-testid="cash-amount-input" className="bg-white border-[#E6E2D8] h-11 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
              </div>
              <p className={`text-xs tabular-nums ${outstanding > 0 ? "text-[#F44336] font-semibold" : change > 0 ? "text-[#5B7059] font-semibold" : "text-[#5C5854]"}`}>
                {outstanding > 0 ? `Outstanding: ${formatINR(outstanding)}` : change > 0 ? `Change to give: ${formatINR(change)}` : effectivePaid > 0 ? "Fully paid" : ""}
              </p>
            </div>
          )}

          {showPaidUI && (
            <div className="flex flex-col gap-3 pt-2 border-t border-dashed border-[#E6E2D8]">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="fully-paid-toggle" className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854] cursor-pointer">Fully paid</Label>
                <Switch id="fully-paid-toggle" checked={fullyPaid} onCheckedChange={handleToggleFullyPaid} data-testid="fully-paid-toggle" className="data-[state=checked]:bg-[#C84C31]" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="paid-input" className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">Paid</Label>
                <Input id="paid-input" type="number" inputMode="decimal" min={0} step="any" placeholder={fullyPaid ? `${computed.total}` : "0"} value={fullyPaid ? "" : paidInput} onChange={(e) => handlePaidInputChange(e.target.value)} data-testid="paid-input" className="bg-white border-[#E6E2D8] h-11 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                <p data-testid="paid-fine-print" className={`text-xs tabular-nums ${outstanding > 0 ? "text-[#F44336] font-semibold" : change > 0 ? "text-[#5B7059] font-semibold" : "text-[#5C5854]"}`}>
                  {outstanding > 0 ? `Outstanding: ${formatINR(outstanding)} (paid − total = −${formatINR(outstanding)})` : change > 0 ? `Change to give: ${formatINR(change)} (paid − total = +${formatINR(change)})` : computed.total > 0 ? `Fully paid (paid − total = ${formatINR(0)})` : ""}
                </p>
              </div>
            </div>
          )}
        </div>

        <Button onClick={handleSave} disabled={saving} data-testid="save-order-button" className="bg-[#C84C31] hover:bg-[#A83C25] text-white font-bold h-12 text-base rounded-lg">
          {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? "Update Order" : "Save Order"}
        </Button>
      </div>
    </div>
  );
}
